import React, { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, FilePdf, Upload, Trash, CheckCircle, Warning, FileText, DownloadSimple } from '@phosphor-icons/react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;
const ax = () => axios.create({ baseURL: API, withCredentials: true });

const MAX_SIZE_MB = 50;
const MAX_SIZE_BYTES = MAX_SIZE_MB * 1024 * 1024;

export default function GameRulesPdfPage() {
  const navigate = useNavigate();
  const [games, setGames] = useState([]);
  const [selectedSlug, setSelectedSlug] = useState('');
  const [pdfs, setPdfs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [title, setTitle] = useState('');
  const [version, setVersion] = useState('');
  const fileRef = useRef(null);

  // Load the games catalog once
  useEffect(() => {
    (async () => {
      try {
        const { data } = await ax().get('/api/games');
        const list = Array.isArray(data) ? data : (data?.games || []);
        setGames(list);
      } catch (e) {
        toast.error('Failed to load games catalog');
      }
    })();
  }, []);

  const loadPdfs = async (slug) => {
    setLoading(true);
    try {
      const { data } = await ax().get('/api/game-pdfs', { params: slug ? { game_slug: slug } : {} });
      setPdfs(Array.isArray(data) ? data : []);
    } catch (e) {
      if (e?.response?.status === 403) {
        toast.error('Admin or Publisher access required');
      } else {
        toast.error('Failed to load PDFs');
      }
      setPdfs([]);
    }
    setLoading(false);
  };
  useEffect(() => { loadPdfs(selectedSlug); }, [selectedSlug]);

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!selectedSlug) {
      toast.error('Pick a game first');
      return;
    }
    if (file.type !== 'application/pdf' && !file.name.toLowerCase().endsWith('.pdf')) {
      toast.error('Only PDF files are accepted');
      return;
    }
    if (file.size > MAX_SIZE_BYTES) {
      toast.error(`File too large (${(file.size / 1024 / 1024).toFixed(1)}MB). Max is ${MAX_SIZE_MB}MB.`);
      return;
    }
    if (!title.trim()) {
      toast.error('Please give the rulebook a title first');
      return;
    }

    setUploading(true);
    const fd = new FormData();
    fd.append('file', file);
    fd.append('game_slug', selectedSlug);
    fd.append('title', title.trim());
    fd.append('version', version.trim());

    try {
      const { data } = await ax().post('/api/game-pdfs/upload', fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 180000,
      });
      toast.success(`Uploaded${data.text_extracted ? ` · ${data.text_length.toLocaleString()} chars extracted for AI grounding` : ' (text extraction failed — file archived)'}`);
      setTitle(''); setVersion('');
      if (fileRef.current) fileRef.current.value = '';
      loadPdfs(selectedSlug);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Upload failed');
    }
    setUploading(false);
  };

  const handleDelete = async (pdfId) => {
    if (!window.confirm('Delete this PDF? The AI will stop using it for grounding.')) return;
    try {
      await ax().delete(`/api/game-pdfs/${pdfId}`);
      toast.success('PDF removed');
      loadPdfs(selectedSlug);
    } catch (e) {
      toast.error('Delete failed');
    }
  };

  const handleDownload = (pdfId) => {
    // Server sends inline; open in a new tab. Auth handled by cookies.
    window.open(`${API}/api/game-pdfs/${pdfId}/download`, '_blank');
  };

  return (
    <div className="app-container" data-testid="game-pdfs-page">
      <header className="p-6 pb-3">
        <div className="flex items-center gap-3 mb-4">
          <button onClick={() => navigate(-1)} className="w-10 h-10 rounded-full bg-[#181824] flex items-center justify-center" data-testid="back-button">
            <ArrowLeft size={20} weight="bold" className="text-white" />
          </button>
          <div>
            <h1 className="text-xl font-bold text-white flex items-center gap-2" style={{ fontFamily: 'Unbounded, sans-serif' }}>
              <FilePdf size={24} weight="duotone" className="text-[#EF4444]" />
              Rulebook PDFs
            </h1>
            <p className="text-[#71717A] text-xs">Ground the AI in official rules · Admin & Publisher only</p>
          </div>
        </div>

        <div className="mb-3">
          <label className="text-[#A1A1AA] text-xs uppercase tracking-wider">Game</label>
          <select className="input-field w-full mt-1" value={selectedSlug} onChange={e => setSelectedSlug(e.target.value)} data-testid="select-game">
            <option value="">All games (I manage)</option>
            {games.map(g => <option key={g.slug} value={g.slug}>{g.name}</option>)}
          </select>
        </div>

        {selectedSlug && (
          <div className="p-3 rounded-xl bg-[#0e0e16] border border-white/[0.06] space-y-2 mb-3">
            <p className="text-white text-xs font-semibold uppercase tracking-wider flex items-center gap-2">
              <Upload size={14} weight="bold" className="text-[#39FF14]" /> Upload a new rulebook
            </p>
            <input className="input-field w-full" placeholder="Title (e.g. Core Rulebook)" value={title} onChange={e => setTitle(e.target.value)} data-testid="input-title" />
            <input className="input-field w-full" placeholder="Version (optional, e.g. 10th ed)" value={version} onChange={e => setVersion(e.target.value)} data-testid="input-version" />
            <label className={`block w-full py-3 rounded-xl text-center font-semibold text-sm cursor-pointer transition-all ${uploading ? 'bg-[#181824] text-[#555]' : 'bg-gradient-to-r from-[#EF4444] to-[#B91C1C] text-white hover:from-[#DC2626] hover:to-[#7F1D1D]'}`} data-testid="upload-label">
              {uploading ? 'Uploading & extracting text…' : `Choose PDF (max ${MAX_SIZE_MB}MB)`}
              <input ref={fileRef} type="file" accept="application/pdf,.pdf" className="hidden" onChange={handleUpload} disabled={uploading} data-testid="upload-input" />
            </label>
          </div>
        )}
      </header>

      <div className="px-6 pb-20 space-y-2">
        {loading ? (
          [1, 2].map(i => <div key={i} className="h-20 bg-white/[0.03] rounded-xl animate-pulse" />)
        ) : pdfs.length === 0 ? (
          <div className="text-center py-12">
            <FilePdf size={40} weight="duotone" className="text-[#333] mx-auto mb-2" />
            <p className="text-[#71717A] text-sm">
              {selectedSlug ? 'No PDFs uploaded for this game yet' : 'Pick a game to see or upload PDFs'}
            </p>
          </div>
        ) : (
          pdfs.map(p => (
            <div key={p.id} className="bg-[#0e0e16] border border-white/[0.06] rounded-xl p-3.5 flex items-center gap-3" data-testid={`pdf-${p.id}`}>
              <div className="w-10 h-10 rounded-xl bg-[#EF4444]/10 flex items-center justify-center shrink-0">
                <FilePdf size={20} weight="duotone" className="text-[#EF4444]" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-white text-sm font-medium truncate">{p.title}{p.version ? ` · ${p.version}` : ''}</p>
                <p className="text-[#71717A] text-[10px] truncate">
                  {(p.size_bytes / 1024 / 1024).toFixed(1)}MB · {p.page_count} pages · game: {p.game_slug}
                </p>
                <div className="flex items-center gap-2 mt-0.5">
                  {p.text_extracted ? (
                    <span className="text-[9px] text-[#39FF14] flex items-center gap-1"><CheckCircle size={9} weight="fill" /> AI grounded ({p.text_length.toLocaleString()} chars)</span>
                  ) : (
                    <span className="text-[9px] text-[#F59E0B] flex items-center gap-1"><Warning size={9} weight="fill" /> Archive only (text extraction failed)</span>
                  )}
                </div>
              </div>
              <button onClick={() => handleDownload(p.id)} className="w-9 h-9 rounded-lg bg-[#181824] border border-white/5 flex items-center justify-center hover:border-white/20" title="Download" data-testid={`pdf-${p.id}-download`}>
                <DownloadSimple size={16} className="text-white" />
              </button>
              <button onClick={() => handleDelete(p.id)} className="w-9 h-9 rounded-lg bg-[#EF4444]/10 border border-[#EF4444]/30 flex items-center justify-center hover:bg-[#EF4444]/20" title="Delete" data-testid={`pdf-${p.id}-delete`}>
                <Trash size={16} className="text-[#EF4444]" />
              </button>
            </div>
          ))
        )}

        {pdfs.length > 0 && (
          <div className="mt-4 p-3 rounded-xl bg-[#00F0FF]/[0.03] border border-[#00F0FF]/10 text-[10px] text-[#71717A] flex items-start gap-2">
            <FileText size={14} weight="duotone" className="text-[#00F0FF] shrink-0 mt-0.5" />
            <span>
              When users ask CyBoard AI a question scoped to this game, the extracted text is
              injected into the model's system prompt as the source of truth — dramatically
              reducing hallucinations. Up to ~30k characters total per game are used per query.
            </span>
          </div>
        )}
      </div>
    </div>
  );
}
